import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl } from '../utils/config';

interface AITask {
  id: string;
  task_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  created_at: string;
  completed_at?: string;
  error_code?: string; // Error code for failed tasks
  page?: string; // Track which page initiated the task
  formData?: any; // Store form data for restoration
  results?: any; // Store task results for completed tasks
}

interface AITaskContextType {
  activeTasks: AITask[];
  addTask: (task: AITask) => void;
  updateTask: (taskId: string, updates: Partial<AITask>) => void;
  removeTask: (taskId: string) => void;
  getTasksByPage: (page: string) => AITask[];
  isTaskRunning: (page: string) => boolean;
  getRecentCompletedTask: (page: string) => AITask | null;
}

const AITaskContext = createContext<AITaskContextType | undefined>(undefined);

export const useAITask = () => {
  const context = useContext(AITaskContext);
  if (context === undefined) {
    throw new Error('useAITask must be used within an AITaskProvider');
  }
  return context;
};

interface AITaskProviderProps {
  children: ReactNode;
}

export const AITaskProvider: React.FC<AITaskProviderProps> = ({ children }) => {
  // Load initial state from localStorage
  const [activeTasks, setActiveTasks] = useState<AITask[]>(() => {
    try {
      const saved = localStorage.getItem('aiTasks');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever tasks change
  useEffect(() => {
    localStorage.setItem('aiTasks', JSON.stringify(activeTasks));
  }, [activeTasks]);

  // Poll for task updates
  useEffect(() => {
    const pollTasks = async () => {
      if (activeTasks.length === 0) return;

      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const tasks = data.tasks || [];

          // Update active tasks with latest status and fetch results for completed tasks without results
          setActiveTasks(prev => 
            prev.map(activeTask => {
              const updatedTask = tasks.find((t: any) => t.id === activeTask.id);
              if (updatedTask) {
                const updated = {
                  ...activeTask,
                  status: updatedTask.status,
                  progress: updatedTask.progress,
                };
                
                // If task is completed and we don't have results yet, fetch them
                if (updatedTask.status === 'completed' && !activeTask.results) {
                  console.log('🎯 Found completed task without results, fetching details:', updatedTask.id);
                  fetchFullTaskDetails(updatedTask.id);
                }
                
                return updated;
              }
              return activeTask;
            })
          );

          // Clean up old completed tasks - keep only the most recent completed task per page for 3 minutes
          setTimeout(() => {
            console.log('🧹 Running cleanup...');
            setActiveTasks(prev => {
              const now = Date.now();
              const threeMinutesAgo = now - (3 * 60 * 1000);
              
              // Group by page
              const tasksByPage: { [page: string]: AITask[] } = {};
              prev.forEach(task => {
                if (!task.page) return;
                if (!tasksByPage[task.page]) tasksByPage[task.page] = [];
                tasksByPage[task.page].push(task);
              });
              
              const filteredTasks: AITask[] = [];
              
              Object.keys(tasksByPage).forEach(page => {
                const pageTasks = tasksByPage[page];
                
                // Keep all running tasks
                const runningTasks = pageTasks.filter(task => 
                  task.status === 'pending' || task.status === 'processing'
                );
                filteredTasks.push(...runningTasks);
                
                // Keep only the most recent completed task if it's less than 3 minutes old
                const completedTasks = pageTasks
                  .filter(task => task.status === 'completed')
                  .sort((a, b) => {
                    const aTime = new Date(a.completed_at || a.created_at).getTime();
                    const bTime = new Date(b.completed_at || b.created_at).getTime();
                    return bTime - aTime;
                  });
                
                if (completedTasks.length > 0) {
                  const mostRecent = completedTasks[0];
                  const completedTime = new Date(mostRecent.completed_at || mostRecent.created_at).getTime();
                  console.log('⏰ Checking task age:', { 
                    taskId: mostRecent.id, 
                    completedTime: new Date(completedTime), 
                    threeMinutesAgo: new Date(threeMinutesAgo),
                    isValid: completedTime > threeMinutesAgo 
                  });
                  
                  if (completedTime > threeMinutesAgo) {
                    filteredTasks.push(mostRecent);
                  } else {
                    console.log('🗑️ Removing expired task:', mostRecent.id);
                  }
                }
              });
              
              console.log('🧹 Cleanup result:', filteredTasks.length, 'tasks remaining');
              return filteredTasks;
            });
          }, 10000); // Run cleanup every 10 seconds instead of 1 second
        }
      } catch (error) {
        console.error('Error polling tasks:', error);
      }
    };

    const interval = setInterval(pollTasks, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [activeTasks.length]);

  const addTask = (task: AITask) => {
    setActiveTasks(prev => [...prev, task]);
  };

  const updateTask = (taskId: string, updates: Partial<AITask>) => {
    console.log('🔄 Updating task:', taskId, 'with:', updates);
    setActiveTasks(prev => {
      const updated = prev.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      );
      console.log('🔄 Updated tasks:', updated);
      return updated;
    });
  };

  const removeTask = (taskId: string) => {
    setActiveTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const getTasksByPage = (page: string) => {
    return activeTasks.filter(task => task.page === page);
  };

  const isTaskRunning = (page: string) => {
    return activeTasks.some(task => 
      task.page === page && 
      (task.status === 'pending' || task.status === 'processing')
    );
  };

  const fetchFullTaskDetails = async (taskId: string) => {
    try {
      console.log('🔄 Fetching full task details:', taskId);
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const taskData = await response.json();
        console.log('📋 Full task data:', taskData);
        console.log('🔍 Task output_media_file_ids:', taskData.output_media_file_ids);
        
        // Check if task has output media file IDs
        if (taskData.output_media_file_ids && taskData.output_media_file_ids.length > 0) {
          console.log('🔗 Found media file IDs, fetching URLs:', taskData.output_media_file_ids);
          
          // Fetch media file details to get URLs
          const mediaPromises = taskData.output_media_file_ids.map(async (fileId: string) => {
            try {
              const mediaResponse = await fetch(`${getApiUrl()}/api/v1/media/${fileId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
              if (mediaResponse.ok) {
                const mediaData = await mediaResponse.json();
                return mediaData.file_url;
              }
            } catch (error) {
              console.error('Error fetching media file:', fileId, error);
            }
            return null;
          });
          
          const imageUrls = (await Promise.all(mediaPromises)).filter(url => url !== null);
          console.log('💾 Storing results for task:', taskId, imageUrls);
          
          // Update the task with results
          setActiveTasks(prev =>
            prev.map(task =>
              task.id === taskId ? {
                ...task,
                status: 'completed',
                completed_at: new Date().toISOString(),
                results: imageUrls
              } : task
            )
          );
        }
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
    }
  };

  const getRecentCompletedTask = (page: string) => {
    const completedTasks = activeTasks
      .filter(task => task.page === page && task.status === 'completed')
      .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime());
    
    console.log('🔍 All active tasks:', activeTasks);
    console.log('🔍 Completed tasks for', page, ':', completedTasks);
    
    return completedTasks.length > 0 ? completedTasks[0] : null;
  };

  return (
    <AITaskContext.Provider value={{
      activeTasks,
      addTask,
      updateTask,
      removeTask,
      getTasksByPage,
      isTaskRunning,
      getRecentCompletedTask,
    }}>
      {children}
    </AITaskContext.Provider>
  );
};